import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getCurrentUser, getReferralStats, inviteFriend, getMyInvitations, sendInvitationSMS } from '../api/auth';
import { updateProfile, gameAPI } from '../api/game';
import { supportTicketsAPI } from '../api/supportTickets';
import SortingHatQuiz from './SortingHatQuiz';
import BlogWriter from './BlogWriter';
import PersianDatePicker from './common/PersianDatePicker';
import '../styles/dashboard.css';
import { sanitizePhoneInput } from '../utils/phone';
import { useTranslation } from 'react-i18next';

const HOUSE_META = {
  Gryffindor: { fa: 'گریفیندور', emoji: '🦁', color: '#740001', accent: '#d3a625', bg: 'rgba(116,0,1,0.18)' },
  Hufflepuff:  { fa: 'هافلپاف',   emoji: '🦡', color: '#f0c040', accent: '#f0c040', bg: 'rgba(240,192,64,0.14)' },
  Ravenclaw:   { fa: 'ریونکلاو',  emoji: '🦅', color: '#0e1a40', accent: '#6fa8dc', bg: 'rgba(14,26,64,0.28)' },
  Slytherin:   { fa: 'اسلیترین',  emoji: '🐍', color: '#1a472a', accent: '#2aae6c', bg: 'rgba(26,71,42,0.22)' },
};

function Stars() {
  return (
    <div className="db-stars" aria-hidden="true">
      {Array.from({ length: 60 }).map((_, i) => (
        <span key={i} className="db-star" style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          '--d': `${(Math.random() * 4 + 1).toFixed(1)}s`,
          '--s': `${(Math.random() * 2 + 1).toFixed(1)}px`,
        }} />
      ))}
    </div>
  );
}

export default function UserDashboard({ onLogout }) {
  const { t } = useTranslation('dashboard');
  const TABS = [
    { key: 'profile',     icon: '👤', label: t('tabs.profile') },
    { key: 'progress',    icon: '📜', label: t('tabs.progress') },
    { key: 'sorting',     icon: '🎩', label: t('tabs.sorting') },
    { key: 'magicImages', icon: '🖼️', label: t('tabs.magicImages') },
    { key: 'referral',    icon: '🔗', label: t('tabs.referral') },
    { key: 'tasks',       icon: '🎯', label: t('tabs.tasks') },
    { key: 'kyc',         icon: '🪪', label: t('tabs.kyc') },
    { key: 'blog',        icon: '✍️', label: t('tabs.blog') },
    { key: 'supportTickets', icon: '🦉', label: t('tabs.supportTickets') },
  ];
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('profile');
  const [profileForm, setProfileForm] = useState({ username: '', birthDate: '', farsiFirstName: '', farsiLastName: '', profileImage: null });
  const [profileMessage, setProfileMessage] = useState({ text: '', ok: false });
  const [savingProfile, setSavingProfile] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [referralStats, setReferralStats] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);
  const [myInvitations, setMyInvitations] = useState([]);
  const [inviteForm, setInviteForm] = useState({ firstName: '', lastName: '', phone: '' });
  const [inviteMsg, setInviteMsg] = useState({ text: '', ok: false });
  const [inviting, setInviting] = useState(false);
  const [smsLoadingId, setSmsLoadingId] = useState(null);
  const [kycForm, setKycForm] = useState({ nationalId: '', birthDate: '' });
  const [kycMsg, setKycMsg]   = useState({ text: '', ok: false });
  const [kycLoading, setKycLoading] = useState(false);
  const [tasks, setTasks] = useState(null);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [taskNotes, setTaskNotes] = useState({});
  const [taskBusyId, setTaskBusyId] = useState(null);
  const [taskMsg, setTaskMsg] = useState({ text: '', ok: false });
  const [telegramLink, setTelegramLink] = useState(null);
  const [telegramLinkLoading, setTelegramLinkLoading] = useState(false);
  const [houseInviteLoading, setHouseInviteLoading] = useState(false);
  const [schoolInviteLoading, setSchoolInviteLoading] = useState(false);
  const [birthdayGreeting, setBirthdayGreeting] = useState(null);

  useEffect(() => { fetchUser(); }, []);

  // منوی پایین موبایل اسکرول افقی دارد — تب فعال را در دید نگه می‌داریم.
  useEffect(() => {
    document
      .querySelector('.db-bottom-nav .db-bottom-btn.active')
      ?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [activeTab]);

  const fetchUser = async () => {
    try {
      const result = await getCurrentUser();
      const data = result.data;
      if (data?.success) {
        setUser(data.user);
        setProfileForm({
          username: data.user.username || '',
          birthDate: data.user.birthDate ? data.user.birthDate.slice(0, 10) : '',
          farsiFirstName: data.user.farsiFirstName || '',
          farsiLastName: data.user.farsiLastName || '',
          profileImage: null,
        });
        if (data.user.birthdayGreeting && data.user.birthdayGreeting.show) {
          setBirthdayGreeting(data.user.birthdayGreeting);
        }
      } else {
        setError('خطا در بارگذاری اطلاعات');
      }
    } catch {
      setError('خطا در اتصال به سرور');
    } finally {
      setLoading(false);
    }
  };

  const closeBirthdayModal = async () => {
    try {
      await gameAPI.completeBirthdayGreeting();
    } catch (err) {
      console.error('Failed to complete birthday greeting:', err);
    }
    setBirthdayGreeting(null);
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileMessage({ text: '', ok: false });
    setSavingProfile(true);
    try {
      const fd = new FormData();
      fd.append('username', profileForm.username);
      fd.append('birthDate', profileForm.birthDate);
      fd.append('farsiFirstName', profileForm.farsiFirstName || '');
      fd.append('farsiLastName', profileForm.farsiLastName || '');
      if (profileForm.profileImage) fd.append('profileImage', profileForm.profileImage);
      const res = await updateProfile(fd);
      const updated = res.data.profile;
      setUser((u) => ({ ...u, ...updated }));
      setProfileMessage({ text: 'پرونده ذخیره شد. با شروع اولین آزمون، اطلاعات قفل می‌شود.', ok: true });
    } catch (err) {
      setProfileMessage({ text: err.response?.data?.message || 'خطا در ذخیره', ok: false });
    } finally {
      setSavingProfile(false);
    }
  };

  const fetchReferralsData = useCallback(async () => {
    setReferralLoading(true);
    try {
      const resStats = await getReferralStats();
      if (resStats.data?.success) setReferralStats(resStats.data.stats);
      const resInv = await getMyInvitations();
      if (resInv.data?.success) setMyInvitations(resInv.data.invitations || []);
    } catch (_) {
    } finally {
      setReferralLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'referral') {
      fetchReferralsData();
    }
  }, [activeTab, fetchReferralsData]);

  // بارگذاری تسک‌های جایزه فقط وقتی تب «تسک‌ها» باز می‌شود
  useEffect(() => {
    if (activeTab !== 'tasks' || tasks) return;
    setTasksLoading(true);
    gameAPI.getRewardTasks()
      .then(res => { if (res.data?.success) setTasks(res.data.tasks || []); })
      .catch(() => {})
      .finally(() => setTasksLoading(false));
  }, [activeTab, tasks]);

  const loadTelegramLink = useCallback(() => {
    setTelegramLinkLoading(true);
    setTelegramLink(null);
    gameAPI.getTelegramLink()
      .then(res => { if (res.data?.success) setTelegramLink(res.data); })
      .catch((err) => setTelegramLink({ success: false, message: err.response?.data?.message || 'ربات تلگرام هنوز فعال نشده است' }))
      .finally(() => setTelegramLinkLoading(false));
  }, []);

  const handleHouseTelegramClick = async (e) => {
    e.preventDefault();
    if (houseInviteLoading) return;
    setHouseInviteLoading(true);
    
    // Open a blank tab immediately to bypass popup blocker
    const newTab = window.open('about:blank', '_blank');
    
    try {
      const res = await gameAPI.getHouseTelegramInvite();
      if (res.data?.success && res.data?.inviteLink) {
        newTab.location.href = res.data.inviteLink;
      } else {
        newTab.close();
        alert(res.data?.message || 'خطا در دریافت لینک گروه تلگرام.');
      }
    } catch (err) {
      newTab.close();
      alert(err.response?.data?.message || 'خطا در برقراری ارتباط با سرور.');
    } finally {
      setHouseInviteLoading(false);
    }
  };

  const handleSchoolTelegramClick = async (e) => {
    e.preventDefault();
    if (schoolInviteLoading) return;
    setSchoolInviteLoading(true);
    
    // Open a blank tab immediately to bypass popup blocker
    const newTab = window.open('about:blank', '_blank');
    
    try {
      const res = await gameAPI.getSchoolTelegramInvite();
      if (res.data?.success && res.data?.inviteLink) {
        newTab.location.href = res.data.inviteLink;
      } else {
        newTab.close();
        alert(res.data?.message || 'خطا در دریافت لینک گروه مدرسه.');
      }
    } catch (err) {
      newTab.close();
      alert(err.response?.data?.message || 'خطا در برقراری ارتباط با سرور.');
    } finally {
      setSchoolInviteLoading(false);
    }
  };


  const submitTask = async (taskId) => {
    setTaskMsg({ text: '', ok: false });
    setTaskBusyId(taskId);
    try {
      const res = await gameAPI.submitRewardTask(taskId, taskNotes[taskId] || '');
      setTaskMsg({ text: res.data.message, ok: true });
      setTasks(null); // بارگذاری دوباره لیست تسک‌ها با وضعیت تازه
      fetchUser(); // بروزرسانی موجودی کیف پول در هدر
    } catch (err) {
      setTaskMsg({ text: err.response?.data?.message || 'خطا در ثبت تسک', ok: false });
    } finally {
      setTaskBusyId(null);
    }
  };

  const handleInviteFriend = async (e) => {
    e.preventDefault();
    setInviteMsg({ text: '', ok: false });
    if (!inviteForm.firstName || !inviteForm.lastName || !inviteForm.phone) {
      setInviteMsg({ text: 'تمامی فیلدها را وارد کنید.', ok: false });
      return;
    }
    setInviting(true);
    try {
      const res = await inviteFriend(inviteForm.firstName, inviteForm.lastName, inviteForm.phone);
      if (res.data?.success) {
        setInviteMsg({ text: res.data.message || 'دعوت با موفقیت ثبت شد.', ok: true });
        setInviteForm({ firstName: '', lastName: '', phone: '' });
        fetchReferralsData();
        fetchUser();
      } else {
        setInviteMsg({ text: res.data?.message || 'خطا در ثبت دعوت.', ok: false });
      }
    } catch (err) {
      setInviteMsg({ text: err.response?.data?.message || 'خطا در اتصال به سرور.', ok: false });
    } finally {
      setInviting(false);
    }
  };

  const handleSendSMS = async (invitationId) => {
    setSmsLoadingId(invitationId);
    try {
      const res = await sendInvitationSMS(invitationId);
      if (res.data?.success) {
        setMyInvitations(prev => prev.map(inv => {
          if (inv._id === invitationId) {
            return { ...inv, smsStatus: 'sent', smsSentAt: new Date() };
          }
          return inv;
        }));
        setInviteMsg({ text: 'پیامک با موفقیت ارسال شد.', ok: true });
      } else {
        setInviteMsg({ text: res.data?.message || 'خطا در ارسال پیامک.', ok: false });
      }
    } catch (err) {
      setInviteMsg({ text: err.response?.data?.message || 'خطا در ارسال پیامک.', ok: false });
    } finally {
      setSmsLoadingId(null);
    }
  };

  const copyReferral = useCallback(() => {
    if (!user?.referralCode) return;
    navigator.clipboard.writeText(user.referralCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [user?.referralCode]);

  const submitKyc = async (e) => {
    e.preventDefault();
    setKycMsg({ text: '', ok: false });
    const nid = kycForm.nationalId.replace(/\D/g, '');
    if (nid.length !== 10) return setKycMsg({ text: 'کد ملی باید ۱۰ رقم باشد.', ok: false });
    if (!/^\d{4}\/\d{2}\/\d{2}$/.test(kycForm.birthDate.trim()))
      return setKycMsg({ text: 'تاریخ تولد را به فرمت ۱۳۷۰/۰۱/۱۵ وارد کنید.', ok: false });
    setKycLoading(true);
    try {
      const token = document.cookie.match(/token=([^;]+)/)?.[1] || localStorage.getItem('token') || '';
      const res = await fetch('/api/auth/kyc/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ nationalId: nid, birthDate: kycForm.birthDate.trim() })
      });
      const data = await res.json();
      if (data.success) {
        setKycMsg({ text: data.message, ok: true });
        fetchUser();
      } else {
        setKycMsg({ text: data.message || 'احراز هویت ناموفق.', ok: false });
      }
    } catch {
      setKycMsg({ text: 'خطا در اتصال به سرور.', ok: false });
    } finally {
      setKycLoading(false);
    }
  };

  const copyInviteLink = useCallback(() => {
    if (!user?.referralCode) return;
    const link = `${window.location.origin}/join?ref=${user.referralCode}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2500);
    });
  }, [user?.referralCode]);

  const house = user?.house;
  const meta = HOUSE_META[house] || null;

  if (loading) {
    return (
      <div className="db-loading">
        <div className="db-loading-wand">🪄</div>
          <p>{t('loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="db-loading">
        <div className="db-error-icon">⚠️</div>
          <p className="db-error-msg">{t('error')}: {error}</p>
        <button className="db-btn-gold" onClick={onLogout}>بازگشت</button>
      </div>
    );
  }

  const displayName = user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'جادوگر';
  const avatarLetter = displayName.slice(0, 1);

  return (
    <div className="db-root" style={meta ? { '--house-color': meta.color, '--house-accent': meta.accent, '--house-bg': meta.bg } : {}}>
      <Stars />

      {/* ── Header ─────────────────────────────────────────── */}
      <header className="db-header">
        <div className="db-header-brand">
          <span className="db-header-crest">⚡</span>
          <span className="db-header-title">{t('title')}</span>
        </div>
        <div className="db-header-right">
          <span className="db-header-wallet">💰 {user.wallet?.display || t('walletDefault')}</span>
          <button className="db-btn-logout" onClick={onLogout}>{t('logout')}</button>
        </div>
      </header>

      <div className="db-layout">
        {/* ── Sidebar (desktop) ──────────────────────────────── */}
        <aside className="db-sidebar">
          {/* Avatar block */}
          <div className="db-sidebar-profile">
            <div className="db-avatar-wrap" style={meta ? { '--ac': meta.accent } : {}}>
              <div className="db-avatar">
                {user.profileImage
                  ? <img src={user.profileImage} alt={displayName} />
                  : <span>{avatarLetter}</span>}
              </div>
              {meta && <span className="db-avatar-badge">{meta.emoji}</span>}
            </div>
            <p className="db-sidebar-name">{displayName}</p>
            {meta && <p className="db-sidebar-house" style={{ color: meta.accent }}>{meta.fa}</p>}
          </div>

          <nav className="db-nav">
            {TABS.map(t => (
              <button
                key={t.key}
                className={`db-nav-btn${activeTab === t.key ? ' active' : ''}`}
                onClick={() => setActiveTab(t.key)}
              >
                <span className="db-nav-icon">{t.icon}</span>
                <span>{t.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* ── Main ───────────────────────────────────────────── */}
        <main className="db-main">

          {/* Hero card */}
          <section className="db-hero">
            <div className="db-hero-glow" />
            <div className="db-hero-left">
              <div className="db-hero-avatar" style={meta ? { '--ac': meta.accent } : {}}>
                {user.profileImage
                  ? <img src={user.profileImage} alt={displayName} />
                  : <span>{avatarLetter}</span>}
                {meta && <span className="db-hero-badge">{meta.emoji}</span>}
              </div>
            </div>
            <div className="db-hero-center">
              <span className="db-hero-label">پرونده جادویی دانش‌آموز</span>
              <h1 className="db-hero-name">{displayName}</h1>
              <div className={`db-lock-badge${user.profileLocked ? ' locked' : ''}`}>
                {user.profileLocked ? '🔒 پروفایل قفل شده' : '✏️ قابل ویرایش تا شروع آزمون'}
              </div>
            </div>
            <div className="db-hero-stats">
              <MagicStat label="خانه"     value={meta ? `${meta.emoji} ${meta.fa}` : null} color={meta?.accent} />
              <MagicStat label="چوبدستی"  value={user.wand} />
              <MagicStat label="پاترونوس" value={user.patronus} />
            </div>
          </section>

          {/* راهنمای عبور از سکو — تا وقتی کاربر به مدرسه نرسیده نمایش داده می‌شود */}
          {!user.hasArrived && (
            <section className="db-journey-banner">
              <span className="db-journey-banner-icon">🚂</span>
              <div className="db-journey-banner-text">
                <strong>هنوز به هاگوارتز نرسیده‌ای!</strong>
                <span>برای تکمیل مسیرت، از سکوی ۹ و ۳/۴ رد شو، سوار قطار هاگوارتز شو و به مدرسه برس. هر وقت خواستی می‌تونی برگردی و داشبوردت رو ببینی.</span>
              </div>
              <Link to="/journey" className="db-btn-gold db-journey-banner-btn">عبور از سکو 9¾</Link>
            </section>
          )}

          {/* Tab content */}
          <div className="db-content">

            {activeTab === 'profile' && (
              <div className="db-tab-panel">
                <h2 className="db-section-title">📋 {t('sectionTitle.profile')}</h2>

                {!user.profileComplete && (
                  <div className="db-alert db-alert-warn">
                    {t('profileIncompleteMessage')}
                  </div>
                )}

                {/* Info card */}
                <div className="db-card">
                  <InfoRow icon="🧙" label={t('labelName')} value={`${user.firstName || ''} ${user.lastName || ''}`.trim()} />
                  <InfoRow icon="📱" label={t('labelPhone')} value={user.phoneNumber} />
                  <InfoRow icon="💰" label={t('labelBalance')} value={<span className="db-currency">{user.wallet?.display || '0K'}</span>} />
                  <InfoRow icon="🔐" label={t('labelProfileStatus')} value={user.profileLocked ? t('profileLocked') : t('profileEditable')} />
                  {user.house && user.houseTelegramLink && (
                    <div style={{ marginTop: '1.2rem', padding: '1rem', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px', border: '1px solid var(--house-accent, #c4a94a)' }}>
                      <p style={{ margin: '0 0 0.6rem 0', fontSize: '0.85rem', color: '#ccc', direction: 'rtl', textAlign: 'right', lineHeight: '1.4' }}>
                        🛡️ شما به خانه <strong>{meta?.fa || user.house}</strong> دعوت شده‌اید. جهت عضویت در گروه تلگرام اختصاصی خانه خود، روی دکمه زیر کلیک کنید:
                      </p>
                      <button
                        onClick={handleHouseTelegramClick}
                        disabled={houseInviteLoading}
                        style={{
                          width: '100%',
                          padding: '0.65rem',
                          background: 'linear-gradient(135deg, var(--house-color, #740001), var(--house-accent, #d3a625))',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: 'bold',
                          fontFamily: 'inherit',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        {houseInviteLoading ? '⏳ در حال ساخت لینک اختصاصی...' : `📨 عضویت در گروه تلگرام ${meta?.fa || user.house}`}
                      </button>
                      <p style={{ margin: '0.6rem 0 0 0', fontSize: '0.75rem', color: '#ff4d4f', direction: 'rtl', textAlign: 'right', fontStyle: 'italic', lineHeight: '1.4', fontWeight: 'bold' }}>
                        ⚠️ توجه: این لینک کاملاً اختصاصی و یک‌بار مصرف است. آن را در اختیار هیچ‌کس قرار ندهید؛ در غیر این‌صورت علاوه بر محرومیت از ورود به گروه، مشمول جریمه سنگین (کسر گالیون) خواهید شد.
                      </p>
                    </div>
                  )}

                  {user.hasArrived && (
                    <div style={{ marginTop: '1.2rem', padding: '1rem', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px', border: '1px solid #c4a94a' }}>
                      <p style={{ margin: '0 0 0.6rem 0', fontSize: '0.85rem', color: '#ccc', direction: 'rtl', textAlign: 'right', lineHeight: '1.4' }}>
                        🏰 شما با موفقیت به هاگوارتز رسیده‌اید! جهت عضویت در گروه تلگرام عمومی مدرسه هاگوارتز، روی دکمه زیر کلیک کنید:
                      </p>
                      <button
                        onClick={handleSchoolTelegramClick}
                        disabled={schoolInviteLoading}
                        style={{
                          width: '100%',
                          padding: '0.65rem',
                          background: 'linear-gradient(135deg, #740001, #d3a625)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: 'bold',
                          fontFamily: 'inherit',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        {schoolInviteLoading ? '⏳ در حال ساخت لینک اختصاصی...' : '📨 عضویت در گروه عمومی مدرسه هاگوارتز'}
                      </button>
                      <p style={{ margin: '0.6rem 0 0 0', fontSize: '0.75rem', color: '#ff4d4f', direction: 'rtl', textAlign: 'right', fontStyle: 'italic', lineHeight: '1.4', fontWeight: 'bold' }}>
                        ⚠️ توجه: این لینک کاملاً اختصاصی و یک‌بار مصرف است. آن را در اختیار هیچ‌کس قرار ندهید؛ در غیر این‌صورت علاوه بر محرومیت از ورود به گروه، مشمول جریمه سنگین (کسر گالیون) خواهید شد.
                      </p>
                    </div>
                  )}
                </div>

                {/* Edit form */}
                {!user.profileLocked && (
                  <div className="db-card" style={{ marginTop: 20 }}>
                    <h3 className="db-card-title">تکمیل پرونده</h3>
                    <form className="db-form" onSubmit={saveProfile}>
                      <label className="db-label">
                        {t('form.usernameLabel')} <span className="db-req">*</span>
                        <input className="db-input" value={profileForm.username}
                          onChange={e => setProfileForm(f => ({ ...f, username: e.target.value }))}
                          minLength={3} required />
                      </label>
                      <label className="db-label">
                        {t('form.birthDateLabel')} <span className="db-req">*</span>
                        <PersianDatePicker
                          className="db-input"
                          value={profileForm.birthDate}
                          outputFormat="gregorian-date-only"
                          onChange={v => setProfileForm(f => ({ ...f, birthDate: v }))}
                          maxDate={new Date()}
                          required
                        />
                      </label>
                      <label className="db-label">
                         {t('form.farsiFirstName')}
                        <input className="db-input" value={profileForm.farsiFirstName}
                          onChange={e => setProfileForm(f => ({ ...f, farsiFirstName: e.target.value }))} />
                      </label>
                      <label className="db-label">
                         {t('form.farsiLastName')}
                        <input className="db-input" value={profileForm.farsiLastName}
                          onChange={e => setProfileForm(f => ({ ...f, farsiLastName: e.target.value }))} />
                      </label>
                      <label className="db-label" style={{ gridColumn: '1/-1' }}>
                         {t('form.profileImage')}
                        <input className="db-input" type="file" accept="image/*"
                          onChange={e => setProfileForm(f => ({ ...f, profileImage: e.target.files?.[0] || null }))}
                          disabled={user.profileImageApproved} />
                         {user.profileImage && <small className="db-hint">{t('profileCurrentHint')}</small>}
                      </label>
                      <div className="db-form-footer">
                         <button className="db-btn-gold" type="submit" disabled={savingProfile}>
                           {savingProfile ? t('saving') : t('saveProfile')}
                        </button>
                        {profileMessage.text && (
                          <p className={`db-form-msg${profileMessage.ok ? ' ok' : ' err'}`}>{profileMessage.text}</p>
                        )}
                      </div>
                    </form>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'progress' && (
              <div className="db-tab-panel">
                <h2 className="db-section-title">📜 مراحل جادویی</h2>
                <div className="db-progress-grid">
                  <ProgressItem done={!!user.isWizard}             icon="⚡" title="جادوگر"          desc="تأیید هویت جادوگری" />
                  <ProgressItem done={!!user.house}                icon="🎩" title="گروه‌بندی"        desc="انتخاب خانه توسط کلاه" />
                  <ProgressItem done={!!user.wand}                 icon="🪄" title="چوبدستی"         desc="آزمون انتخاب چوب‌دستی" />
                  <ProgressItem done={!!user.patronus}             icon="✨" title="پاترونوس"        desc="آزمون پاترونوس" />
                  <ProgressItem done={!!user.hasBoarded}           icon="🚂" title="هاگوارتز اکسپرس" desc="سوار قطار به مدرسه" />
                  <ProgressItem done={user.currentLocation === 'hogwarts'} icon="🏰" title="هاگوارتز" desc="ورود به مدرسه" />
                </div>
              </div>
            )}

            {activeTab === 'sorting' && (
              <div className="db-tab-panel">
                {!user.profileComplete ? (
                  <div className="db-empty-card">
                    <div className="db-empty-icon">🎩</div>
                    <h3>پروفایل ناقص</h3>
                    <p>برای شرکت در آزمون گروه‌بندی ابتدا نام کاربری و تاریخ تولد را ثبت کنید.</p>
                    <button className="db-btn-gold" onClick={() => setActiveTab('profile')}>تکمیل پروفایل</button>
                  </div>
                ) : (
                  <SortingHatQuiz
                    user={user}
                    onCompleted={(data) => setUser(u => ({
                      ...u,
                      house: data.house?.name || u.house,
                      houseInfo: data.house || u.houseInfo,
                      hasPassedSortingHat: true,
                      profileLocked: true,
                      sortingHatScores: data.scores || u.sortingHatScores,
                    }))}
                  />
                )}
              </div>
            )}

            {activeTab === 'magicImages' && (
              <div className="db-tab-panel">
                <h2 className="db-section-title">🖼️ تصاویر جادویی</h2>
                <div className="db-images-grid">
                  {[
                    { label: 'چوبدستی',       icon: '🪄', url: user.generatedImages?.wand },
                    { label: 'پاترونوس',       icon: '✨', url: user.generatedImages?.patronus },
                    { label: 'پروفایل خانه',   icon: '🏠', url: user.generatedImages?.houseProfile },
                    { label: 'تصویر قدی',      icon: '🧙', url: user.generatedImages?.houseFullBody },
                  ].map(({ label, icon, url }) => (
                    <div className="db-image-card" key={label}>
                      <div className="db-image-card-title">{icon} {label}</div>
                      {url ? (
                        <a href={url} target="_blank" rel="noreferrer" className="db-image-link">
                          <img src={url} alt={label} className="db-generated-img" />
                          <span className="db-image-overlay">🔍 مشاهده کامل</span>
                        </a>
                      ) : (
                        <div className="db-image-placeholder">
                          <span>{icon}</span>
                          <p>بعد از تکمیل آزمون مربوطه ساخته می‌شود</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'referral' && (
              <div className="db-tab-panel">
                <h2 className="db-section-title">🔗 سیستم دعوت</h2>

                {referralStats?.referredBy && (
                  <div className="db-card" style={{ padding: '12px 18px', borderRight: '4px solid var(--gold)', background: 'rgba(212,175,55,0.05)', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '1.2rem' }}>🧙</span>
                    <span>معرف شما: <strong>{referralStats.referredBy.firstName} {referralStats.referredBy.lastName}</strong></span>
                  </div>
                )}

                {/* Slots counter — dynamic */}
                {(() => {
                  const remaining = user.inviteSlots ?? 3;
                  const used = referralStats?.totalReferred ?? (user.referredUsersCount ?? 0);
                  const total = remaining + used;
                  return (
                    <div className="db-card db-invite-slots-card">
                      <div className="db-invite-slots-row">
                        <span className="db-invite-slots-label">ظرفیت دعوت باقی‌مانده</span>
                        <div className="db-invite-slots-boxes">
                          {Array.from({ length: total }).map((_, i) => (
                            <span key={i} className={`db-invite-slot-box${i < remaining ? ' used' : ''}`}>
                              {i < remaining ? '✉️' : '🔒'}
                            </span>
                          ))}
                        </div>
                        <span className="db-invite-slots-count">{remaining} / {total}</span>
                      </div>
                      <p className="db-hint" style={{ marginTop: '0.5rem' }}>
                        هر کاربر می‌تواند ۳ نفر دعوت کند.
                        هر ۱۰ نفر در زنجیره دعوتت، ۳ دعوت‌نامه اضافه دریافت می‌کنی.
                      </p>
                    </div>
                  );
                })()}

                {/* Invite link */}
                <div className="db-card db-referral-card">
                  <p className="db-referral-desc">
                    دوستانت را به دنیای جادو دعوت کن.<br />
                    هر نفری که با لینک تو ثبت‌نام کند، هر دو <strong>۱۰ گالیون</strong> دریافت می‌کنید.
                  </p>

                  <label className="db-label">کد دعوت</label>
                  <div className="db-referral-code-wrap" onClick={copyReferral} title="برای کپی کلیک کن">
                    <span className="db-referral-code">{user.referralCode || '---'}</span>
                    <span className="db-copy-btn">{copied ? '✅ کپی شد' : '📋 کپی'}</span>
                  </div>

                  <label className="db-label" style={{ marginTop: '1rem' }}>لینک دعوت</label>
                  <div className="db-invite-link-row">
                    <span className="db-invite-link-text" dir="ltr">
                      {window.location.host}/join?ref={user.referralCode}
                    </span>
                    <button className="db-btn-gold db-copy-link-btn" onClick={copyInviteLink}>
                      {copied ? t('copied') : t('copy')}
                    </button>
                  </div>
                </div>

                {/* Invite Friend Form */}
                <div className="db-card" style={{ marginTop: '1rem' }}>
                  <h3 className="db-card-title">✉️ دعوت مستقیم دوست با پیامک</h3>
                  <form className="db-form" onSubmit={handleInviteFriend}>
                    <label className="db-label">
                      نام دوست
                      <input
                        className="db-input"
                        placeholder="مثال: رون"
                        value={inviteForm.firstName}
                        onChange={e => setInviteForm(f => ({ ...f, firstName: e.target.value }))}
                        required
                      />
                    </label>
                    <label className="db-label">
                      نام خانوادگی دوست
                      <input
                        className="db-input"
                        placeholder="مثال: ویزلی"
                        value={inviteForm.lastName}
                        onChange={e => setInviteForm(f => ({ ...f, lastName: e.target.value }))}
                        required
                      />
                    </label>
                    <label className="db-label" style={{ gridColumn: '1 / -1' }}>
                      شماره موبایل
                      <input
                        className="db-input"
                        placeholder="مثال: 09123456789"
                        value={inviteForm.phone}
                        onChange={e => setInviteForm(f => ({ ...f, phone: sanitizePhoneInput(e.target.value) }))}
                        dir="ltr"
                        required
                      />
                    </label>
                    <div className="db-form-footer">
                      <button className="db-btn-gold" type="submit" disabled={inviting || (user.inviteSlots ?? 3) <= 0}>
                        {inviting ? '⏳ در حال ثبت...' : '🪄 ثبت و ارسال دعوت‌نامه'}
                      </button>
                      {inviteMsg.text && (
                        <p className={`db-form-msg ${inviteMsg.ok ? 'ok' : 'err'}`}>{inviteMsg.text}</p>
                      )}
                    </div>
                  </form>
                </div>

                {/* Direct Invites List with Codes */}
                <div className="db-card" style={{ marginTop: '1rem' }}>
                  <h3 className="db-card-title">📋 دعوت‌های مستقیم شما</h3>
                  {referralLoading && <p className="db-hint">در حال بارگذاری...</p>}
                  {!referralLoading && myInvitations.length === 0 && (
                    <p className="db-hint">هنوز هیچ دوستی را مستقیماً دعوت نکرده‌اید.</p>
                  )}
                  {!referralLoading && myInvitations.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {myInvitations.map((inv) => (
                        <div key={inv._id} style={{
                          padding: '1rem',
                          background: 'rgba(255,255,255,0.03)',
                          borderRadius: '12px',
                          border: '1px solid rgba(255,255,255,0.08)'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                            <strong>👤 {inv.recipientFirstName} {inv.recipientLastName} ({inv.recipientPhone})</strong>
                            <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)' }}>
                              ثبت شده در: {new Date(inv.createdAt).toLocaleDateString('fa-IR')}
                            </span>
                          </div>
                          
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', margin: '12px 0', fontSize: '0.85rem' }}>
                            <div style={{ background: 'rgba(0,0,0,0.2)', padding: '8px 12px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.05)' }}>
                              <span style={{ color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: '4px' }}>کد دعوت:</span>
                              <code style={{ fontSize: '1rem', color: 'var(--gold)' }}>{inv.code}</code>
                            </div>
                            <div style={{ background: 'rgba(0,0,0,0.2)', padding: '8px 12px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.05)' }}>
                              <span style={{ color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: '4px' }}>کد بلیت قطار:</span>
                              <code style={{ fontSize: '1rem', color: 'var(--gold)' }}>{inv.ticketCode}</code>
                            </div>
                          </div>

                          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
                            {(inv.smsStatus === 'sent' || inv.smsSentAt) ? (
                              <span style={{ color: '#4caf50', fontSize: '0.85rem' }}>✓ پیامک ارسال شد</span>
                            ) : (
                              <button
                                className="db-btn-gold"
                                style={{ padding: '6px 16px', fontSize: '0.8rem' }}
                                onClick={() => handleSendSMS(inv._id)}
                                disabled={smsLoadingId === inv._id}
                              >
                                {smsLoadingId === inv._id ? '⏳ در حال ارسال...' : '✉️ ارسال پیامک دعوت'}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Friends invited — list with details */}
                <div className="db-card">
                  <h3 className="db-card-title">
                    👥 دوستان دعوت‌شده
                    {referralStats && (
                      <span style={{ fontSize: '0.85rem', color: 'var(--house-accent, #c4a94a)', marginRight: '0.5rem' }}>
                        ({referralStats.totalReferred} نفر)
                      </span>
                    )}
                  </h3>
                  {referralLoading && <p className="db-hint">در حال بارگذاری...</p>}
                  {!referralLoading && (!referralStats || referralStats.totalReferred === 0) && (
                    <p className="db-hint">هنوز کسی را دعوت نکرده‌ای. اولین دعوت‌نامه را بفرست!</p>
                  )}
                  {!referralLoading && referralStats?.referredUsers?.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
                      {referralStats.referredUsers.map((friend, idx) => {
                        const houseEmoji = { Gryffindor: '🦁', Slytherin: '🐍', Ravenclaw: '🦅', Hufflepuff: '🦡' };
                        return (
                          <div key={idx} style={{
                            display: 'flex', alignItems: 'center', gap: '0.75rem',
                            padding: '0.6rem 0.85rem',
                            background: 'rgba(255,255,255,0.04)',
                            borderRadius: '10px',
                            border: '1px solid rgba(255,255,255,0.08)'
                          }}>
                            <span style={{ fontSize: '1.4rem' }}>
                              {friend.house ? (houseEmoji[friend.house] || '🧙') : '🧙'}
                            </span>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                {friend.firstName} {friend.lastName}
                              </div>
                              {friend.house && (
                                <div style={{ fontSize: '0.75rem', color: 'var(--house-accent, #c4a94a)' }}>
                                  {friend.house}
                                </div>
                              )}
                            </div>
                             <span style={{ fontSize: '0.75rem', color: friend.isWizard === true ? '#4caf50' : friend.hasFailed ? '#ff4d4f' : 'rgba(255,255,255,0.4)' }}>
                               {friend.isWizard === true ? '⚡ جادوگر' : friend.hasFailed ? '🧱 ناموفق در عبور' : '⏳ در راه'}
                             </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Telegram channel */}
                <div className="db-card db-telegram-card">
                  <div className="db-telegram-header">
                    <span className="db-telegram-icon">✈️</span>
                    <div>
                      <h3 className="db-card-title" style={{ margin: 0 }}>کانال تلگرام</h3>
                      <p className="db-hint" style={{ margin: 0 }}>
                        عضو کانال ما شو — هر هفته <strong>۵ بلیت رایگان</strong> بین اعضا قرعه‌کشی می‌شه!
                      </p>
                    </div>
                  </div>
                  <a
                    className="db-btn-gold db-telegram-btn"
                    href="https://t.me/WwWPotterHeadIR"
                    target="_blank"
                    rel="noreferrer"
                  >
                    عضویت در @WwWPotterHeadIR
                  </a>
                </div>

              </div>
            )}

            {activeTab === 'tasks' && (
              <div className="db-tab-panel">
                <h2 className="db-section-title">🎯 تسک‌های جایزه</h2>
                <p className="db-hint">با انجام این تسک‌ها گالیون به کیف پول جادویی‌ات اضافه می‌شود.</p>

                {taskMsg.text && (
                  <div className="db-card" style={{ borderColor: taskMsg.ok ? 'rgba(76,175,80,0.4)' : 'rgba(244,67,54,0.4)', marginBottom: '1rem' }}>
                    <p style={{ color: taskMsg.ok ? '#4caf50' : '#f44336', margin: 0 }}>{taskMsg.text}</p>
                  </div>
                )}

                {tasksLoading && <p className="db-hint">در حال بارگذاری...</p>}
                {!tasksLoading && tasks?.length === 0 && (
                  <p className="db-hint">در حال حاضر تسک فعالی وجود ندارد.</p>
                )}

                {!tasksLoading && tasks?.map(task => {
                  const submission = task.submission;
                  const isTelegram = task.key === 'telegram_join_channel';
                  const isKyc = task.key === 'kyc_verification';
                  const isProfilePhoto = task.key === 'profile_photo_approved';
                  const isAppInstall = task.key === 'app_install';
                  // تسک‌های «system_verified» (تلگرام/احراز هویت/عکس پروفایل/نصب اپ) هرگز از فرم
                  // عمومی «ارسال برای بررسی» قابل ثبت نیستند — فقط فرآیند امن خودشان
                  // (ربات/زوهال/تایید دستی ادمین) می‌تواند آن‌ها را کامل کند؛ سمت سرور
                  // هم submitRewardTask این تسک‌ها را رد می‌کند، این فقط UI را هماهنگ می‌کند.
                  const isSystemVerified = task.completionMethod === 'system_verified';
                  const status = submission?.status;
                  const isOpen = status !== 'approved' && status !== 'pending';

                  return (
                    <div key={task._id} className="db-card" style={{ marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
                        <div>
                          <h3 className="db-card-title" style={{ margin: 0 }}>{task.farsiTitle || task.title}</h3>
                          {task.farsiDescription && (
                            <p className="db-hint" style={{ margin: '0.35rem 0 0' }}>{task.farsiDescription}</p>
                          )}
                        </div>
                        <span style={{ fontWeight: 700, color: '#c9a84c', whiteSpace: 'nowrap' }}>
                          🎁 {task.reward?.display}
                        </span>
                      </div>

                      {status === 'approved' && (
                        <p style={{ color: '#4caf50', marginTop: '0.75rem', marginBottom: 0 }}>✅ دریافت شد</p>
                      )}
                      {status === 'pending' && (
                        <p style={{ color: '#c9a84c', marginTop: '0.75rem', marginBottom: 0 }}>⏳ در انتظار بررسی ادمین/استاد</p>
                      )}
                      {status === 'rejected' && (
                        <p style={{ color: '#f44336', marginTop: '0.75rem', marginBottom: '0.5rem' }}>❌ رد شد — می‌توانی دوباره تلاش کنی</p>
                      )}

                      {isOpen && isTelegram && (
                        <div style={{ marginTop: '0.75rem' }}>
                          {!telegramLink && (
                            <button className="db-btn-gold" disabled={telegramLinkLoading} onClick={loadTelegramLink}>
                              {telegramLinkLoading ? 'در حال آماده‌سازی...' : '🚀 شروع ربات تلگرام'}
                            </button>
                          )}
                          {telegramLink?.success === false && (
                            <p style={{ color: '#f44336', marginTop: '0.5rem' }}>{telegramLink.message}</p>
                          )}
                          {telegramLink?.deepLink && (
                            <a className="db-btn-gold" href={telegramLink.deepLink} target="_blank" rel="noreferrer">
                              ✈️ باز کردن ربات در تلگرام
                            </a>
                          )}
                          <p className="db-hint" style={{ marginTop: '0.5rem' }}>
                            بعد از استارت زدن ربات، عضو کانال شو و در تلگرام دستور <code>/check</code> را بفرست — جایزه خودکار (بدون نیاز به تایید ادمین) واریز می‌شود.
                          </p>
                        </div>
                      )}

                      {isOpen && isSystemVerified && !isTelegram && (
                        <div style={{ marginTop: '0.75rem' }}>
                          <p className="db-hint" style={{ marginBottom: '0.5rem' }}>
                            {isKyc
                              ? 'این جایزه بعد از احراز هویت موفق در تب «احراز هویت» به‌طور خودکار واریز می‌شود.'
                              : isProfilePhoto
                                ? 'این جایزه بعد از تایید عکس پروفایل توسط ادمین/استاد به‌طور خودکار واریز می‌شود.'
                                : isAppInstall
                                  ? 'این جایزه پس از نصب اپلیکیشن اندروید و ورود به حساب کاربری به‌طور خودکار واریز می‌شود.'
                                  : 'این تسک به‌صورت خودکار توسط سیستم بررسی و تکمیل می‌شود.'}
                          </p>
                          {isKyc && (
                            <button className="db-btn-gold" onClick={() => setActiveTab('kyc')}>رفتن به تب احراز هویت</button>
                          )}
                          {isProfilePhoto && (
                            <button className="db-btn-gold" onClick={() => setActiveTab('profile')}>رفتن به تب پروفایل</button>
                          )}
                        </div>
                      )}

                      {isOpen && !isTelegram && !isSystemVerified && (
                        <div style={{ marginTop: '0.75rem' }}>
                          <textarea
                            className="db-input"
                            rows={2}
                            placeholder="لینک پست/استوری/توییت را همراه با توضیح کوتاه اینجا بگذار..."
                            value={taskNotes[task._id] || ''}
                            onChange={(e) => setTaskNotes(n => ({ ...n, [task._id]: e.target.value }))}
                            style={{ width: '100%', resize: 'vertical', marginBottom: '0.5rem', display: 'block' }}
                          />
                          <button
                            className="db-btn-gold"
                            disabled={taskBusyId === task._id || !(taskNotes[task._id] || '').trim()}
                            onClick={() => submitTask(task._id)}
                          >
                            {taskBusyId === task._id ? 'در حال ارسال...' : 'ارسال برای بررسی'}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {activeTab === 'kyc' && (
              <div className="db-tab-panel">
                <h2 className="db-section-title">🪪 احراز هویت</h2>

                {user.kycStatus === 'verified' ? (
                  <div className="db-card" style={{ textAlign: 'center', padding: '2rem' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>✅</div>
                    <h3 style={{ color: '#4caf50', marginBottom: '0.5rem' }}>احراز هویت تأیید شده</h3>
                    <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.9rem' }}>
                      هویت شما تأیید شده و ۱۰ گالیون به کیف پول شما افزوده شده است.
                    </p>
                    {user.kycVerifiedAt && (
                      <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                        تاریخ: {new Date(user.kycVerifiedAt).toLocaleDateString('fa-IR')}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="db-card">
                    <div style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: '10px', padding: '1rem', marginBottom: '1.5rem' }}>
                      <p style={{ color: '#c9a84c', fontWeight: 600, marginBottom: '0.4rem' }}>🎁 پاداش احراز هویت: ۱۰ گالیون</p>
                      <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', lineHeight: 1.7 }}>
                        با تأیید هویت خود (کد ملی + تاریخ تولد) ۱۰ گالیون طلایی به کیف پول جادویی‌تان اضافه می‌شود.
                        این اطلاعات فقط برای تطبیق با شماره موبایل ثبت‌شده استفاده می‌شود و ذخیره نمی‌شود.
                      </p>
                    </div>

                    <form onSubmit={submitKyc} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)' }}>
                          کد ملی
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={10}
                          placeholder="مثلاً: 0012345678"
                          value={kycForm.nationalId}
                          onChange={e => setKycForm(f => ({ ...f, nationalId: e.target.value }))}
                          style={{ width: '100%', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(0,0,0,0.3)', color: '#fff', fontSize: '1rem', letterSpacing: '0.1em', direction: 'ltr', textAlign: 'center' }}
                          disabled={kycLoading}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)' }}>
                          تاریخ تولد (شمسی)
                        </label>
                        <PersianDatePicker
                          className="pdp-input-dark"
                          placeholder="مثلاً: ۱۳۷۰/۰۱/۱۵"
                          value={kycForm.birthDate}
                          outputFormat="jalali"
                          onChange={v => setKycForm(f => ({ ...f, birthDate: v }))}
                          maxDate={new Date()}
                          disabled={kycLoading}
                        />
                        <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.35)', marginTop: '0.3rem' }}>
                          دقیقاً همان تاریخ تولد ثبت‌شده در ثبت‌احوال را انتخاب کنید — بر اساس تقویم شمسی.
                        </p>
                      </div>

                      {kycMsg.text && (
                        <div style={{ padding: '0.65rem 0.85rem', borderRadius: '8px', fontSize: '0.88rem', background: kycMsg.ok ? 'rgba(76,175,80,0.15)' : 'rgba(244,67,54,0.15)', border: `1px solid ${kycMsg.ok ? 'rgba(76,175,80,0.4)' : 'rgba(244,67,54,0.4)'}`, color: kycMsg.ok ? '#81c784' : '#ef9a9a' }}>
                          {kycMsg.ok ? '✅ ' : '⚠️ '}{kycMsg.text}
                        </div>
                      )}

                      <button
                        type="submit"
                        className="db-btn-gold"
                        disabled={kycLoading || !kycForm.nationalId || !kycForm.birthDate}
                        style={{ marginTop: '0.5rem' }}
                      >
                        {kycLoading ? 'در حال بررسی...' : '🪪 تأیید هویت و دریافت ۱۰ گالیون'}
                      </button>
                    </form>

                    {user.kycStatus === 'failed' && (
                      <p style={{ marginTop: '1rem', fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>
                        آخرین تلاش ناموفق بود — می‌توانید دوباره امتحان کنید.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'blog' && (
              <div className="db-tab-panel">
                <h2 className="db-section-title">✍️ نوشتن مقاله</h2>
                <div className="db-card">
                  <BlogWriter />
                </div>
              </div>
            )}

            {activeTab === 'supportTickets' && (
              <div className="db-tab-panel">
                <SupportTicketsPanel />
              </div>
            )}

          </div>{/* db-content */}
        </main>
      </div>{/* db-layout */}

      {/* ── Bottom tab bar (mobile) ──────────────────────────── */}
      <nav className="db-bottom-nav">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`db-bottom-btn${activeTab === t.key ? ' active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            <span>{t.icon}</span>
            <span>{t.label}</span>
          </button>
        ))}
      </nav>

      {/* Birthday Greeting Modal */}
      {birthdayGreeting && (
        <div className="db-birthday-modal-overlay">
          <div className="db-birthday-modal-card">
            <div className="db-birthday-modal-sparkles">✨ 🎉 ⚡ 🦉 ✨</div>
            <h2 className="db-birthday-modal-title">تولدت مبارک! 🎉</h2>
            <div className="db-birthday-modal-body">
              {birthdayGreeting.message.split('\n').map((line, idx) => (
                <p key={idx}>{line}</p>
              ))}
            </div>
            {birthdayGreeting.giftName && (
              <div className="db-birthday-modal-gift">
                <span className="db-birthday-modal-gift-icon">🎁</span>
                <div className="db-birthday-modal-gift-details">
                  <strong>هدیه جادویی شما: {birthdayGreeting.giftName}</strong>
                  <span>محصول به وسایل شما اضافه شد! برای استفاده کلیک کنید:</span>
                  {birthdayGreeting.giftLink && (
                    <a href={birthdayGreeting.giftLink} target="_blank" rel="noreferrer" className="db-birthday-modal-gift-link">
                      دریافت هدیه 🔗
                    </a>
                  )}
                </div>
              </div>
            )}
            <button className="db-btn-gold db-birthday-modal-close" onClick={closeBirthdayModal}>
              ممنون! 🪄
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Small helpers ───────────────────────────────────────────── */
function MagicStat({ label, value, color }) {
  return (
    <div className={`db-magic-stat${value ? ' revealed' : ''}`} style={color ? { borderColor: `${color}55` } : {}}>
      <small>{label}</small>
      <strong style={color ? { color } : {}}>{value || <span className="db-magic-empty">هنوز آشکار نشده</span>}</strong>
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="db-info-row">
      <span className="db-info-label">{icon} {label}</span>
      <span className="db-info-value">{value}</span>
    </div>
  );
}

function ProgressItem({ done, icon, title, desc }) {
  return (
    <div className={`db-progress-item${done ? ' done' : ''}`}>
      <div className="db-progress-icon">{done ? '✅' : icon}</div>
      <div className="db-progress-body">
        <strong>{title}</strong>
        <span>{desc}</span>
      </div>
    </div>
  );
}

const TICKET_CATEGORIES = {
  feature: { label: 'معجون یا طلسم جدید (ویژگی جدید)', emoji: '🧪' },
  bug: { label: 'کرم فلوبر در دیگ (گزارش باگ)', emoji: '🐛' },
  financial: { label: 'حساب گالئون‌ها در گرینگوتز (مشکل مالی)', emoji: '🪙' },
  technical: { label: 'شکستگی چوب‌دستی (مشکل فنی)', emoji: '🪄' },
  question: { label: 'سوال از هرماینی (سوال)', emoji: '🦉' },
  other: { label: 'رازهای مکتوم (موارد دیگر)', emoji: '📜' }
};

const TICKET_STATUSES = {
  pending: { label: 'در انتظار پرواز جغد (در انتظار بررسی)', color: '#ff9800' },
  todo: { label: 'ثبت در طومار کارها (لیست کار)', color: '#f0c040' },
  in_progress: { label: 'طلسم‌خوانی و اقدام (در حال انجام)', color: '#2196f3' },
  resolved: { label: 'پاسخ دریافت شد (حل شده)', color: '#4caf50' },
  closed: { label: 'بایگانی‌شده در وزارتخانه (بسته شده)', color: '#9e9e9e' }
};

function SupportTicketsPanel() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState({ text: '', ok: false });
  const [formData, setFormData] = useState({ title: '', category: 'question', description: '' });
  const [selectedTicket, setSelectedTicket] = useState(null);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await supportTicketsAPI.getUserTickets();
      if (res.data?.success) {
        setTickets(res.data.tickets || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg({ text: '', ok: false });
    if (!formData.title.trim() || !formData.description.trim()) {
      return setMsg({ text: 'تمامی فیلدها را تکمیل کنید.', ok: false });
    }
    setSubmitting(true);
    try {
      const res = await supportTicketsAPI.createTicket(formData);
      if (res.data?.success) {
        setMsg({ text: 'جغد نامه شما با موفقیت به پرواز درآمد!', ok: true });
        setFormData({ title: '', category: 'question', description: '' });
        fetchTickets();
        setTimeout(() => {
          setShowForm(false);
          setMsg({ text: '', ok: false });
        }, 2000);
      }
    } catch (err) {
      setMsg({ text: err.response?.data?.message || 'خطا در فرستادن جغد.', ok: false });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <h2 className="db-section-title" style={{ margin: 0 }}>🦉 صندوق پست جغدی (پشتیبانی)</h2>
        <button className="db-btn-gold" onClick={() => { setShowForm(!showForm); setSelectedTicket(null); }}>
          {showForm ? '📯 مشاهده نامه‌ها' : '✉️ فرستادن جغد جدید'}
        </button>
      </div>

      {showForm ? (
        <div className="db-card">
          <h3 className="db-card-title">ارسال جغد جدید به مدرسه</h3>
          <form className="db-form" onSubmit={handleSubmit}>
            <label className="db-label">
              موضوع نامه
              <input 
                className="db-input" 
                placeholder="مثلاً: گم شدن گالئون‌ها در بازار دیگون"
                value={formData.title}
                onChange={e => setFormData(f => ({ ...f, title: e.target.value }))}
                required
              />
            </label>

            <label className="db-label">
              نوع آواز پرنده (دسته‌بندی پیام)
              <select 
                className="db-input"
                style={{ background: 'rgba(0,0,0,0.3)', color: '#fff' }}
                value={formData.category}
                onChange={e => setFormData(f => ({ ...f, category: e.target.value }))}
                required
              >
                {Object.entries(TICKET_CATEGORIES).map(([key, cat]) => (
                  <option key={key} value={key} style={{ background: '#1a1a2e', color: '#fff' }}>
                    {cat.emoji} {cat.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="db-label" style={{ gridColumn: '1/-1' }}>
              متن نامه جادویی (شرح کامل مشکل)
              <textarea 
                className="db-input"
                rows={5}
                placeholder="لطفاً مشکل خود را به تفصیل بنویسید..."
                value={formData.description}
                onChange={e => setFormData(f => ({ ...f, description: e.target.value }))}
                required
              />
            </label>

            {msg.text && (
              <div style={{ 
                gridColumn: '1/-1', 
                padding: '0.65rem 0.85rem', 
                borderRadius: '8px', 
                background: msg.ok ? 'rgba(76,175,80,0.15)' : 'rgba(244,67,54,0.15)', 
                border: `1px solid ${msg.ok ? 'rgba(76,175,80,0.4)' : 'rgba(244,67,54,0.4)'}`, 
                color: msg.ok ? '#81c784' : '#ef9a9a' 
              }}>
                {msg.ok ? '✅ ' : '⚠️ '}{msg.text}
              </div>
            )}

            <div className="db-form-footer" style={{ gridColumn: '1/-1' }}>
              <button className="db-btn-gold" type="submit" disabled={submitting}>
                {submitting ? '⏳ در حال پرواز جغد...' : '🚀 به پرواز درآوردن جغد'}
              </button>
            </div>
          </form>
        </div>
      ) : selectedTicket ? (
        <div className="db-card">
          <button className="db-btn-gold" style={{ marginBottom: '1rem', padding: '0.3rem 0.7rem', fontSize: '0.85rem' }} onClick={() => setSelectedTicket(null)}>
            🔙 بازگشت به لیست نامه‌ها
          </button>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
            <h3 className="db-card-title" style={{ margin: 0 }}>{selectedTicket.title}</h3>
            <span style={{ 
              padding: '0.2rem 0.6rem', 
              borderRadius: '6px', 
              fontSize: '0.8rem', 
              background: TICKET_STATUSES[selectedTicket.status]?.color + '22',
              border: `1px solid ${TICKET_STATUSES[selectedTicket.status]?.color}`,
              color: TICKET_STATUSES[selectedTicket.status]?.color 
            }}>
              {TICKET_STATUSES[selectedTicket.status]?.label}
            </span>
          </div>

          <div style={{ display: 'flex', gap: '1rem', fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)', marginBottom: '1rem', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '0.5rem' }}>
            <span>نوع: {TICKET_CATEGORIES[selectedTicket.category]?.emoji} {TICKET_CATEGORIES[selectedTicket.category]?.label}</span>
            <span>تاریخ ارسال: {new Date(selectedTicket.createdAt).toLocaleDateString('fa-IR')}</span>
          </div>

          <div style={{ background: 'rgba(0,0,0,0.2)', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem', border: '1px solid rgba(255,255,255,0.05)', whiteSpace: 'pre-wrap' }}>
            <strong>📝 متن پیام شما:</strong>
            <p style={{ marginTop: '0.5rem', color: 'rgba(255,255,255,0.85)', lineHeight: 1.6 }}>{selectedTicket.description}</p>
          </div>

          {selectedTicket.aiAnswered && (
            <div style={{ 
              background: 'rgba(114, 47, 55, 0.1)', 
              border: '1px dashed #722f37', 
              borderRadius: '8px', 
              padding: '1.2rem', 
              marginBottom: '1.5rem',
              boxShadow: '0 4px 12px rgba(114, 47, 55, 0.15)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#c9a84c', fontWeight: 600, fontSize: '0.95rem', marginBottom: '0.5rem' }}>
                <span>🧙‍♂️ پاسخ جادویی آلبوس دامبلدور (پاسخ هوشمند):</span>
              </div>
              <p style={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.7, fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>{selectedTicket.aiAnswer}</p>
            </div>
          )}

          {selectedTicket.adminNotes ? (
            <div style={{ 
              background: 'rgba(76, 175, 80, 0.08)', 
              border: '1px solid rgba(76, 175, 80, 0.3)', 
              borderRadius: '8px', 
              padding: '1.2rem', 
              whiteSpace: 'pre-wrap'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#81c784', fontWeight: 600, fontSize: '0.95rem', marginBottom: '0.5rem' }}>
                <span>🏰 پاسخ رسمی دفتر مدیریت هاگوارتز:</span>
              </div>
              <p style={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.7 }}>{selectedTicket.adminNotes}</p>
            </div>
          ) : (
            !selectedTicket.aiAnswered && (
              <p style={{ color: 'rgba(255,255,255,0.4)', fontStyle: 'italic', textAlign: 'center', marginTop: '1rem' }}>
                🦉 جغد نامه شما هنوز به دفتر مدیریت نرسیده است یا در حال نوشتن پاسخ هستند. صبور باشید...
              </p>
            )
          )}
        </div>
      ) : (
        <div className="db-card">
          <h3 className="db-card-title">نامه‌های پرواز کرده</h3>
          {loading ? (
            <p className="db-hint">در حال فراخوانی نامه‌ها...</p>
          ) : tickets.length === 0 ? (
            <p className="db-hint">تاکنون جغدی برای پشتیبانی نفرستاده‌اید.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {tickets.map(t => (
                <div 
                  key={t._id} 
                  onClick={() => setSelectedTicket(t)}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.85rem 1rem',
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    flexWrap: 'wrap',
                    gap: '0.5rem'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.07)';
                    e.currentTarget.style.borderColor = 'var(--house-accent, #c9a84c)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                    <strong style={{ fontSize: '0.95rem' }}>{t.title}</strong>
                    <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.45)' }}>
                      نوع: {TICKET_CATEGORIES[t.category]?.emoji} {TICKET_CATEGORIES[t.category]?.label} | تاریخ: {new Date(t.createdAt).toLocaleDateString('fa-IR')}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {t.aiAnswered && (
                      <span title="پاسخ خودکار هوش مصنوعی" style={{ fontSize: '1.2rem' }}>🧙‍♂️</span>
                    )}
                    <span style={{ 
                      fontSize: '0.78rem', 
                      padding: '0.15rem 0.5rem', 
                      borderRadius: '6px',
                      background: TICKET_STATUSES[t.status]?.color + '15',
                      border: `1px solid ${TICKET_STATUSES[t.status]?.color}`,
                      color: TICKET_STATUSES[t.status]?.color
                    }}>
                      {TICKET_STATUSES[t.status]?.label.split(' (')[0]}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
